import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useAppAuth } from '@/contexts/app-auth.context'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Heading, Text } from '@/components/ui/typography'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  SearchDropdown,
  type SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Users,
  MoreHorizontal,
  Pencil,
  Trash2,
  TriangleAlert,
} from 'lucide-react'
import {
  directoryApiClient,
  type Staff,
  type RoleWithMembers,
  type ScheduleEntry,
  type DeleteImpact,
} from '@/lib/api/directory.client'
import { directoryAdminApiClient } from '@/lib/api/directory-admin.client'
import { contactsApiClient } from '@/lib/api/contacts.client'
import { integrationsClient } from '@/lib/api/integrations/integrations.client'
import { StaffRolesCell } from '@/components/directory/StaffRolesCell'
import { formatPhoneWithExtension, validatePhoneE164 } from '@/lib/validators'
import {
  INTEGRATIONS,
  getDirectoryAssigneeField,
  type ContactWithRelationships,
} from '@super/shared-types'
import type { Integration } from '@super/shared-types'
import { formatPhone, formatRelativeTime } from './formatters'
import TimeOffSection from './schedule/TimeOffSection'

type TabValue = 'staff' | 'roles'

export function TeamSettingsPage() {
  const { currentCompany, isSuperAdmin } = useAppAuth()
  const companyId = currentCompany?.id

  // Tab lives in the URL (?tab=roles) so deep links and the role-detail back
  // link land on the right subtab, and refresh keeps it.
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const activeTab: TabValue = tabParam === 'roles' ? 'roles' : 'staff'
  const setActiveTab = (tab: TabValue) =>
    setSearchParams(tab === 'staff' ? {} : { tab }, { replace: true })
  const [staff, setStaff] = useState<Staff[]>([])
  const [roles, setRoles] = useState<RoleWithMembers[]>([])
  const [schedule, setSchedule] = useState<ScheduleEntry[]>([])
  const [loading, setLoading] = useState(true)

  const refreshAll = async () => {
    if (!companyId) return
    setLoading(true)
    try {
      const [staffList, rolesList, scheduleList] = await Promise.all([
        isSuperAdmin
          ? directoryAdminApiClient.listStaff(companyId)
          : directoryApiClient.listStaff(),
        isSuperAdmin
          ? directoryAdminApiClient.listRoles(companyId)
          : directoryApiClient.listRoles(),
        isSuperAdmin
          ? directoryAdminApiClient.listSchedule(companyId)
          : directoryApiClient.listSchedule(),
      ])
      setStaff(Array.isArray(staffList) ? staffList : [])
      setRoles(Array.isArray(rolesList) ? rolesList : [])
      setSchedule(Array.isArray(scheduleList) ? scheduleList : [])
    } catch (e) {
      console.error(e)
      toast.error('Failed to load directory')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refreshAll()
  }, [companyId])

  return (
    <div className="h-full overflow-y-auto p-8 mt-16">
      <div className="max-w-6xl mx-auto">
        <Heading variant="page-title" className="mb-2">
          Team Directory
        </Heading>
        <Text variant="muted" className="mb-6">
          Staff and Roles. Action groups reference Roles or Staff — changes
          propagate everywhere.
        </Text>

        <div className="flex gap-2 mb-6 border-b">
          {(
            [
              ['staff', 'Staff'],
              ['roles', 'Roles'],
            ] as const
          ).map(([tabValue, tabLabel]) => (
            <button
              key={tabValue}
              onClick={() => setActiveTab(tabValue)}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                activeTab === tabValue
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tabLabel}
            </button>
          ))}
        </div>

        {loading && <DirectorySkeleton />}

        {!loading && activeTab === 'staff' && (
          <StaffTab
            staff={staff}
            roles={roles}
            schedule={schedule}
            companyId={companyId ?? ''}
            onChange={refreshAll}
          />
        )}
        {!loading && activeTab === 'roles' && (
          <RolesTab
            roles={roles}
            companyId={companyId ?? ''}
            onChange={refreshAll}
          />
        )}
      </div>
    </div>
  )
}

function DirectorySkeleton() {
  return (
    <div className="space-y-3">
      {[...Array(4)].map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  )
}

// ─── Staff tab ────────────────────────────────────────────────────────────

function StaffTab(props: {
  staff: Staff[]
  roles: RoleWithMembers[]
  schedule: ScheduleEntry[]
  companyId: string
  onChange: () => void
}) {
  const { staff, roles, schedule, companyId, onChange } = props
  const { isSuperAdmin } = useAppAuth()
  const [editing, setEditing] = useState<Staff | null>(null)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<Staff | null>(null)

  const rolesByStaffId = useMemo(() => {
    const map = new Map<string, RoleWithMembers[]>()
    for (const role of roles) {
      for (const m of role.members) {
        const list = map.get(m.staff.id)
        if (list) list.push(role)
        else map.set(m.staff.id, [role])
      }
    }
    return map
  }, [roles])

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <Heading variant="subsection-title">Staff ({staff.length})</Heading>
        <Button onClick={() => setCreating(true)}>Add staff</Button>
      </div>

      <TooltipProvider delayDuration={150}>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="text-left px-4 py-3 font-medium">Phone</th>
                <th className="text-left px-4 py-3 font-medium">Email</th>
                <th className="text-left px-4 py-3 font-medium">Roles</th>
                <th className="text-left px-4 py-3 font-medium">Updated</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.map(staffMember => (
                <tr key={staffMember.id} className="border-t">
                  <td className="px-4 py-3 font-medium">{staffMember.name}</td>
                  <td className="px-4 py-3">
                    {staffMember.phone ? formatPhone(staffMember.phone) : '—'}
                  </td>
                  <td className="px-4 py-3">{staffMember.email ?? '—'}</td>
                  <td className="px-4 py-3">
                    <StaffRolesCell
                      roles={rolesByStaffId.get(staffMember.id) ?? []}
                    />
                  </td>
                  <td
                    className="px-4 py-3 text-muted-foreground"
                    title={staffMember.updatedAt}
                  >
                    {formatRelativeTime(staffMember.updatedAt)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-40">
                        <DropdownMenuItem
                          onClick={() => setEditing(staffMember)}
                        >
                          <Pencil className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setDeleting(staffMember)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
              {staff.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    No staff yet. Click "Add staff" to create one.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </TooltipProvider>

      <TimeOffSection
        schedule={schedule}
        roles={roles}
        staff={staff}
        companyId={companyId}
        onChange={onChange}
      />

      {creating && (
        <StaffEditDialog
          mode="create"
          companyId={companyId}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            onChange()
          }}
        />
      )}
      {editing && (
        <StaffEditDialog
          mode="edit"
          staff={editing}
          companyId={companyId}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            onChange()
          }}
        />
      )}
      {deleting && (
        <DeleteImpactDialog
          title={`Delete ${deleting.name}?`}
          description="This staff member will be removed. References in roles and action groups will be cleared automatically — changes propagate everywhere."
          fetchImpact={() =>
            isSuperAdmin
              ? directoryAdminApiClient.previewStaffDelete(
                  companyId,
                  deleting.id,
                )
              : directoryApiClient.previewStaffDelete(deleting.id)
          }
          renderImpact={impact => (
            <ul className="text-sm space-y-1 list-disc pl-5">
              <li>
                {impact.roleMemberships ?? 0} role membership(s) will be removed
              </li>
              <li>
                {impact.actionContacts ?? 0} action group link(s) will be
                removed
              </li>
            </ul>
          )}
          onConfirm={async () => {
            if (isSuperAdmin) {
              await directoryAdminApiClient.deleteStaff(companyId, deleting.id)
            } else {
              await directoryApiClient.deleteStaff(deleting.id)
            }
            toast.success(`Deleted ${deleting.name}`)
            setDeleting(null)
            onChange()
          }}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  )
}

function StaffEditDialog(props: {
  mode: 'create' | 'edit'
  staff?: Staff
  companyId: string
  onClose: () => void
  onSaved: () => void
}) {
  const { mode, staff, companyId, onClose, onSaved } = props
  const { isSuperAdmin } = useAppAuth()
  const [name, setName] = useState(staff?.name ?? '')
  const [phone, setPhone] = useState(() =>
    staff?.phone ? formatPhoneWithExtension(staff.phone) : '',
  )
  const [phoneError, setPhoneError] = useState<string | null>(null)
  const [email, setEmail] = useState(staff?.email ?? '')
  const [saving, setSaving] = useState(false)

  const [crmContacts, setCrmContacts] = useState<ContactWithRelationships[]>([])

  useEffect(() => {
    if (mode !== 'create' || !companyId) return
    const load = isSuperAdmin
      ? contactsApiClient.getAllAdminContactsForCompany(companyId)
      : contactsApiClient.listForAuthedCompany()
    load
      .then(setCrmContacts)
      .catch(error => console.error('Failed to load CRM contacts', error))
  }, [mode, companyId, isSuperAdmin])

  // Per-integration push assignee: one optional free-text
  // field per integration the company has active that supports it (Yardi
  // Voyager today). Hidden entirely when no such integration is active.
  const [assigneeIntegrations, setAssigneeIntegrations] = useState<
    Integration[]
  >([])
  const [assigneeValues, setAssigneeValues] = useState<Record<string, string>>(
    () =>
      Object.fromEntries(
        (staff?.integrationAssignees ?? []).map(a => [
          a.integrationType,
          a.value,
        ]),
      ),
  )

  useEffect(() => {
    if (!companyId) return
    const load = isSuperAdmin
      ? integrationsClient.getStaffAssigneeEnabledIntegrationsAdmin(companyId)
      : integrationsClient.getStaffAssigneeEnabledIntegrations()
    load
      .then(setAssigneeIntegrations)
      .catch(error =>
        console.error(
          'Failed to load staff-assignee-enabled integrations',
          error,
        ),
      )
  }, [companyId, isSuperAdmin])

  const crmItems: SearchDropdownItem<ContactWithRelationships>[] = useMemo(
    () =>
      crmContacts.map(contact => {
        const full = [contact.firstName, contact.lastName]
          .filter(Boolean)
          .join(' ')
          .trim()
        const phone = contact.primaryPhoneNumber ?? ''
        const email = contact.primaryEmail ?? ''
        return {
          id: contact.id,
          label: full || phone || email || 'Unnamed contact',
          sublabel: [phone, email].filter(Boolean).join(' · '),
          data: contact,
        }
      }),
    [crmContacts],
  )

  const handleCrmSelect = (
    item:
      | SearchDropdownItem<ContactWithRelationships>
      | SearchDropdownItem<ContactWithRelationships>[]
      | null,
  ) => {
    if (!item || Array.isArray(item) || !item.data) return
    const c = item.data
    const full = [c.firstName, c.lastName].filter(Boolean).join(' ').trim()
    if (full) setName(full)
    if (c.primaryPhoneNumber) {
      setPhone(formatPhoneWithExtension(c.primaryPhoneNumber))
      setPhoneError(null)
    }
    if (c.primaryEmail) setEmail(c.primaryEmail)
  }

  const handlePhoneBlur = () => {
    const trimmed = phone.trim()
    if (!trimmed) {
      setPhoneError(null)
      return
    }
    const result = validatePhoneE164(trimmed)
    if (!result) {
      setPhoneError(`"${trimmed}" is not a valid phone number`)
      return
    }
    setPhone(formatPhoneWithExtension(trimmed))
    setPhoneError(null)
  }

  const save = async () => {
    if (!name.trim()) return toast.error('Name is required')
    const phoneTrimmed = (phone ?? '').trim()
    const emailTrimmed = (email ?? '').trim()
    if (!phoneTrimmed && !emailTrimmed) {
      return toast.error('Phone or email is required')
    }

    let normalizedPhone: string | undefined
    if (phoneTrimmed) {
      const result = validatePhoneE164(phoneTrimmed)
      if (!result) {
        setPhoneError(`"${phoneTrimmed}" is not a valid phone number`)
        return
      }
      // Reconstruct the stored form (base + optional #extension) — the backend
      // persists it as one string, e.g. "+15551234567#103".
      normalizedPhone = result.extension
        ? `${result.value}#${result.extension}`
        : result.value
    }

    // Only the integrations currently active/rendered — an entry here always
    // means "set to this value" (empty string clears it); one not currently
    // active/rendered is simply omitted and left untouched server-side.
    const integrationAssignees = assigneeIntegrations.map(integration => ({
      integrationType: integration.type,
      value: (assigneeValues[integration.type] ?? '').trim(),
    }))

    setSaving(true)
    try {
      if (mode === 'create') {
        const createInput = {
          name: name.trim(),
          phone: normalizedPhone,
          email: emailTrimmed || undefined,
          integrationAssignees,
        }
        if (isSuperAdmin) {
          await directoryAdminApiClient.createStaff(companyId, createInput)
        } else {
          await directoryApiClient.createStaff(createInput)
        }
        toast.success('Staff created')
      } else if (staff) {
        // null clears a field; undefined would be stripped from JSON and kept.
        const updateInput = {
          name: name.trim(),
          phone: normalizedPhone ?? null,
          email: emailTrimmed || null,
          integrationAssignees,
        }
        if (isSuperAdmin) {
          await directoryAdminApiClient.updateStaff(
            companyId,
            staff.id,
            updateInput,
          )
        } else {
          await directoryApiClient.updateStaff(staff.id, updateInput)
        }
        toast.success('Staff updated — changes propagate everywhere')
      }
      onSaved()
    } catch (e: any) {
      console.error(e)
      toast.error(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create'
              ? 'Add staff member'
              : `Edit ${staff?.name ?? 'staff'}`}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? isSuperAdmin
                ? 'Create a staff record manually or pick someone from the CRM.'
                : 'Create a staff record.'
              : 'Changes propagate to every Role and action group this person is in.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {mode === 'create' && (
            <div>
              <Label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                Quick add from CRM
              </Label>
              <SearchDropdown<ContactWithRelationships>
                items={crmItems}
                onSelect={handleCrmSelect}
                placeholder="Search CRM contacts…"
                searchPlaceholder="Search by name, phone, or email"
                icon={<Users className="h-4 w-4 text-muted-foreground" />}
                emptyMessage="No matching CRM contacts"
                buttonClassName="w-full justify-between"
              />
              <Text variant="muted" className="text-xs mt-1.5">
                Picking a contact prefills the fields below — you can still edit
                them.
              </Text>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="staff-name" className="mb-1.5 block">
                Full name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="staff-name"
                placeholder="e.g. Asim Patel"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="staff-phone" className="mb-1.5 block">
                Phone
              </Label>
              <Input
                id="staff-phone"
                placeholder="+1 (555) 123-4567 #103"
                value={phone ?? ''}
                onChange={e => {
                  setPhone(e.target.value)
                  if (phoneError) setPhoneError(null)
                }}
                onBlur={handlePhoneBlur}
                aria-invalid={phoneError ? true : undefined}
                className={
                  phoneError
                    ? 'border-destructive focus-visible:ring-destructive'
                    : undefined
                }
              />
              {phoneError ? (
                <p className="mt-1 text-xs text-destructive" role="alert">
                  {phoneError}
                </p>
              ) : (
                <Text variant="muted" className="text-xs mt-1.5">
                  Add an extension with #, e.g. #103.
                </Text>
              )}
            </div>

            <div>
              <Label htmlFor="staff-email" className="mb-1.5 block">
                Email
              </Label>
              <Input
                id="staff-email"
                type="email"
                placeholder="name@example.com"
                value={email ?? ''}
                onChange={e => setEmail(e.target.value)}
              />
              <Text variant="muted" className="text-xs mt-1.5">
                Phone or email is required.
              </Text>
            </div>

            {assigneeIntegrations.map(integration => {
              const integrationName =
                (
                  INTEGRATIONS[
                    integration.type as keyof typeof INTEGRATIONS
                  ] as { NAME?: string } | undefined
                )?.NAME ?? integration.type
              // Label + guidance come from the integration's own assignee
              // handoff field, so each integration explains what to enter
              // (Yardi: the employee name on the Labor tab).
              const field = getDirectoryAssigneeField(integration.type)
              const fieldLabel = field?.label ?? 'Assignee'
              const description =
                field?.description ??
                `This person's identifier in ${integrationName}. Optional.`
              return (
                <div key={integration.id}>
                  <Label
                    htmlFor={`staff-assignee-${integration.id}`}
                    className="mb-1.5 block"
                  >
                    {integrationName} — {fieldLabel}
                  </Label>
                  <Input
                    id={`staff-assignee-${integration.id}`}
                    placeholder="Optional"
                    value={assigneeValues[integration.type] ?? ''}
                    onChange={e =>
                      setAssigneeValues(prev => ({
                        ...prev,
                        [integration.type]: e.target.value,
                      }))
                    }
                  />
                  <Text variant="muted" className="text-xs mt-1.5">
                    {description}
                  </Text>
                </div>
              )
            })}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Roles tab ────────────────────────────────────────────────────────────

function RolesTab(props: {
  roles: RoleWithMembers[]
  companyId: string
  onChange: () => void
}) {
  const { roles, companyId, onChange } = props
  const { isSuperAdmin } = useAppAuth()
  const navigate = useNavigate()
  const [deleting, setDeleting] = useState<RoleWithMembers | null>(null)

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <Heading variant="subsection-title">Roles ({roles.length})</Heading>
        {/* Nothing is created until Save on the manage page. */}
        <Button onClick={() => navigate('/settings/team/new')}>Add role</Button>
      </div>

      <div className="space-y-3">
        {roles.map(role => (
          <div key={role.id} className="border rounded-lg p-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="flex items-center gap-2">
                  <Heading variant="subsection-title">{role.name}</Heading>
                  <Badge variant="secondary">
                    {role.members.length} members
                  </Badge>
                  {role.schedulingMode === 'ROTATION' &&
                    !role.rotationFallbackStaffId && (
                      <TooltipProvider delayDuration={150}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-amber-600 dark:text-amber-500">
                              <TriangleAlert className="h-4 w-4" />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            This on-call rotation has no fallback — it won't
                            reach anyone on days that are not explicitly
                            assigned.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                </div>
                {role.description && (
                  <Text variant="muted">{role.description}</Text>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(`/settings/team/${role.id}`)}
                >
                  Manage role
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-40">
                    <DropdownMenuItem
                      onClick={() => navigate(`/settings/team/${role.id}`)}
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setDeleting(role)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            {role.members.length > 0 ? (
              <div className="flex flex-wrap gap-2 mt-2">
                <TooltipProvider delayDuration={200}>
                  {role.members.map(member => (
                    <Tooltip key={member.id}>
                      <TooltipTrigger asChild>
                        <Badge variant="outline" className="cursor-default">
                          {member.staff.name}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="text-xs space-y-0.5">
                          {member.staff.email && (
                            <div>{member.staff.email}</div>
                          )}
                          {member.staff.phone && (
                            <div>{formatPhone(member.staff.phone)}</div>
                          )}
                          {!member.staff.email && !member.staff.phone && (
                            <div>No contact info</div>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </TooltipProvider>
              </div>
            ) : (
              <Text variant="muted" className="text-sm italic">
                No one is in this role yet. Add members or rules tagging this
                role won't notify anyone.
              </Text>
            )}
          </div>
        ))}
        {roles.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            No roles yet. Click "Add role" to create one.
          </div>
        )}
      </div>

      {deleting && (
        <DeleteImpactDialog
          title={`Delete role "${deleting.name}"?`}
          description="This role will be removed. Memberships and action group links pointing at it will be cleared — propagation, no orphans."
          fetchImpact={() =>
            isSuperAdmin
              ? directoryAdminApiClient.previewRoleDelete(
                  companyId,
                  deleting.id,
                )
              : directoryApiClient.previewRoleDelete(deleting.id)
          }
          renderImpact={impact => (
            <ul className="text-sm space-y-1 list-disc pl-5">
              <li>{impact.members ?? 0} member link(s) will be removed</li>
              <li>
                {impact.actionContacts ?? 0} action group link(s) will be
                removed
              </li>
            </ul>
          )}
          onConfirm={async () => {
            if (isSuperAdmin) {
              await directoryAdminApiClient.deleteRole(companyId, deleting.id)
            } else {
              await directoryApiClient.deleteRole(deleting.id)
            }
            toast.success(`Deleted role "${deleting.name}"`)
            setDeleting(null)
            onChange()
          }}
          onClose={() => setDeleting(null)}
        />
      )}
    </div>
  )
}

// ─── Shared confirm-with-impact dialog ────────────────────────────────────

function DeleteImpactDialog(props: {
  title: string
  description: string
  fetchImpact: () => Promise<DeleteImpact>
  renderImpact: (impact: DeleteImpact) => React.ReactNode
  onConfirm: () => Promise<void>
  onClose: () => void
}) {
  const { title, description, fetchImpact, renderImpact, onConfirm, onClose } =
    props
  const [impact, setImpact] = useState<DeleteImpact | null>(null)
  const [confirming, setConfirming] = useState(false)

  useEffect(() => {
    fetchImpact()
      .then(setImpact)
      .catch(() => setImpact({}))
  }, [])

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="py-3">
          {impact == null ? (
            <Skeleton className="h-12 w-full" />
          ) : (
            renderImpact(impact)
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={async () => {
              setConfirming(true)
              try {
                await onConfirm()
              } finally {
                setConfirming(false)
              }
            }}
            disabled={confirming || impact == null}
          >
            {confirming ? 'Deleting…' : 'Confirm delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
